import React, { useState } from "react";
import __ from "src/utils/Translations";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import { Typography } from "@mui/material";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import PositionsTable from "src/Pages/Company/OrganizationStructure/Positions/Table/PositionsTable";
import { useFetchStructureData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchStructureData";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructurePositionAdd } from "src/utils/RoleBasedSecurity/permissions";
import PositionModal from "src/Pages/Company/OrganizationStructure/Positions/Modal/PositionModal";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";

const Positions = () => {
  const isAhr = useIsAhr();
  const companyName = useCompanyName();
  const companyId = useCompanyId();
  const logStatus = useLogStatus();
  const { data: structureData, error: structureError } =
    useFetchStructureData(companyId);
  const structureId: string | undefined = structureData?.[0]?.structureId;

  if (structureError) {
    const error = structureError as AxiosError;
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "Positions: fetchStructureData failed",
      statusDetails: {
        source: "API",
        operation: "fetchStructureData",
        status: error.response?.status,
        url: error.config?.url,
      },
    });
  }

  const hasPermissionPositionAdd = usePermission(
    organizationStructurePositionAdd
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({
    label: title,
    to: link,
  }));

  const breadcrumbs = isAhr
    ? [
        {
          label: `${__("Zarządzanie firmą")} ${companyName}`,
          to: `/ahr`,
        },
        {
          label: __("Struktura organizacji"),
          to: `/ahr/organization-structure`,
        },
        {
          label: __("Stanowiska"),
          to: `/ahr/organization-structure/positions`,
        },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Struktura organizacji"),
          to: `/company/edit/${companyId}/organization-structure`,
        },
        {
          label: __("Stanowiska"),
          to: `/company/edit/${companyId}/organization-structure/positions`,
        },
      ];

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <BackButton sx={{ mt: 4, mb: 3, p: 0 }} />
      <Typography
        sx={{
          fontSize: "24px",
          textTransform: "upperCase",
          fontWeight: "fontWeightBold",
          mb: 4,
        }}
      >
        {__("Stanowiska")}
      </Typography>

      <PositionsTable
        companyId={companyId}
        structureId={structureId!}
        onAddClick={
          hasPermissionPositionAdd
            ? () => setIsCreateModalOpen(true)
            : undefined
        }
      />

      {isCreateModalOpen && (
        <PositionModal
          companyId={companyId}
          structureId={structureId!}
          onClose={() => setIsCreateModalOpen(false)}
          mode="create"
        />
      )}
    </>
  );
};

export default Positions;
